import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Fade,
  Slide,
  CircularProgress,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { ref, onValue, off } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";

function toDateMaybe(value) {
  if (!value) return null;
  // aceita timestamp number, ISO string, ou "2026-08-24T20:00:00Z"
  const d =
    typeof value === "number" ? new Date(value) : new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

function getTemporalStatus(ev) {
  const now = new Date();
  const start = toDateMaybe(ev?.timing?.startAtMs ?? ev?.startAtMs);
  const end = toDateMaybe(ev?.timing?.endAtMs ?? ev?.endAtMs);

  if (!start && !end) return null;

  if (start && now < start) return "agendado";
  if (start && end && now >= start && now <= end) return "ao_vivo";
  if (end && now > end) return "encerrado";

  if (start && now >= start) return "encerrado";

  return null;
}

function formatPtDate(ev) {
  const start = toDateMaybe(ev?.timing?.startAtMs ?? ev?.startAtMs);
  if (!start) return "";
  return start.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getBadgeStyle(status) {
  // mantém teu glass + cor por estado
  if (status === "ao_vivo") {
    return {
      label: "Ao vivo",
      sx: {
        background: "rgba(20, 220, 140, 0.14)",
        border: "1px solid rgba(20, 220, 140, 0.26)",
      },
    };
  }
  if (status === "agendado") {
    return {
      label: "Agendado",
      sx: {
        background: "rgba(82, 170, 255, 0.14)",
        border: "1px solid rgba(82, 170, 255, 0.26)",
      },
    };
  }
  if (status === "encerrado") {
    return {
      label: "Encerrado",
      sx: {
        background: "rgba(255, 183, 77, 0.12)",
        border: "1px solid rgba(255, 183, 77, 0.22)",
      },
    };
  }
  return null;
}

function pickCoverImage(ev) {
  return (
    ev?.coverImage ||
    ev?.coverUrl ||
    ev?.bannerUrl ||
    ev?.image ||
    "https://images.unsplash.com/photo-1492684223066-81342ee5ff30"
  );
}

function getMinPriceLabel(ev) {
  const min = Number(ev?.stats?.minTicketPrice ?? ev?.minTicketPrice ?? 0);
  return min.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function FeaturedEvents() {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const eventsRef = ref(rtdb, "events");

    const unsub = onValue(
      eventsRef,
      (snap) => {
        const val = snap.val();
        const list = val
          ? Object.entries(val).map(([id, ev]) => ({ id, ...ev }))
          : [];

        // só online
        const online = list.filter((ev) => ev?.status === "online");

        // ordena por startAt crescente (se tiver)
        online.sort((a, b) => {
          const da = toDateMaybe(a?.startAt || a?.dateStart)?.getTime() ?? 0;
          const db = toDateMaybe(b?.startAt || b?.dateStart)?.getTime() ?? 0;
          return da - db;
        });

        setEvents(online);
        setLoading(false);
      },
      (err) => {
        console.error("RTDB events error:", err);
        setLoading(false);
      },
    );

    return () => {
      // onValue no v9 retorna função de unsubscribe;
      // mesmo assim garantimos cleanup padrão
      if (typeof unsub === "function") unsub();
      off(eventsRef);
    };
  }, []);

  const mapped = useMemo(() => {
    return events.map((ev) => {
      const temporal = getTemporalStatus(ev);
      const badge = getBadgeStyle(temporal);

      const city = ev.location?.city || ev.city || "";
      const state = ev.location?.state || ev.state || "";
      const location = city && state ? `${city} · ${state}` : city;

      return {
        id: ev.id,
        slug: ev.seo?.slug || ev.slug || ev.id,
        title: ev.title || "Evento",
        category: ev.category || "Evento",
        date: formatPtDate(ev),
        location,
        price: getMinPriceLabel(ev),
        image: pickCoverImage(ev),
        badge,
      };
    });
  }, [events]);

  return (
    <Box sx={{ py: { xs: 6, md: 7 } }}>
      <Container
        maxWidth={false}
        sx={{ maxWidth: "1120px", px: { xs: 2, sm: 2.5 } }}
      >
        {/* HEADER */}
        <Slide direction="up" in timeout={600}>
          <Box sx={{ mb: 3 }}>
            <Fade in timeout={800}>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    letterSpacing: "0.12em",
                    fontSize: 12,
                    opacity: 0.8,
                    mb: 1,
                  }}
                >
                  EM DESTAQUE
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: "-0.02em",
                    mb: 1,
                  }}
                >
                  Eventos em destaque
                </Typography>

                <Typography
                  sx={{
                    maxWidth: "60ch",
                    opacity: 0.75,
                    lineHeight: 1.6,
                  }}
                >
                  Descubra os eventos mais procurados do momento e garanta seu
                  ingresso com segurança e transparência.
                </Typography>
              </Box>
            </Fade>
          </Box>
        </Slide>

        {/* LOADING / EMPTY */}
        {loading ? (
          <Box sx={{ py: 6, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : mapped.length === 0 ? (
          <Box
            sx={{
              border: "1px solid var(--linha)",
              background: "rgba(255,255,255,0.04)",
              borderRadius: 2,
              p: 3,
            }}
          >
            <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
              Nenhum evento online agora
            </Typography>
            <Typography sx={{ opacity: 0.75 }}>
              Assim que um organizador publicar, ele aparece aqui
              automaticamente.
            </Typography>
          </Box>
        ) : (
          /* GRID */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {mapped.map((ev, idx) => (
              <Slide key={ev.id} direction="up" in timeout={600 + idx * 120}>
                <Fade in timeout={800 + idx * 120}>
                  <Box
                    component={RouterLink}
                    to={`/eventos/${ev.id}`}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: { md: 2, xs: 2 },
                      overflow: "hidden",
                      border: "1px solid var(--linha)",
                      background: "rgba(255,255,255,0.04)",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                      transition: "all .35s ease",
                      textDecoration: "none",
                      color: "inherit",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 18px 60px rgba(0,0,0,0.38)",
                      },
                    }}
                  >
                    {/* IMAGE */}
                    <Box
                      sx={{
                        height: 180,
                        backgroundImage: `url(${ev.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        position: "relative",
                      }}
                    >
                      {/* badge temporal (Agendado / Ao vivo / Encerrado) */}
                      {ev.badge ? (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            px: 1.2,
                            py: 0.55,
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 950,
                            backdropFilter: "blur(10px)",
                            ...ev.badge.sx,
                          }}
                        >
                          {ev.badge.label}
                        </Box>
                      ) : null}
                    </Box>

                    {/* BODY */}
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                      }}
                    >
                      <Box
                        sx={{
                          alignSelf: "flex-start",
                          px: 1.2,
                          py: 0.5,
                          mb: 1,
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 900,
                          background: "rgba(18,212,122,0.14)",
                          border: "1px solid rgba(18,212,122,0.22)",
                        }}
                      >
                        {ev.category}
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: 18,
                          mb: 0.5,
                        }}
                      >
                        {ev.title}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          opacity: 0.7,
                          mb: 1.5,
                        }}
                      >
                        {ev.date ? ev.date : "Data a definir"}
                        {ev.location ? ` · ${ev.location}` : ""}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: "auto" }}
                      >
                        <Typography sx={{ fontWeight: 900, fontSize: 16 }}>
                          {ev.price}
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 12,
                              opacity: 0.6,
                              ml: 0.5,
                            }}
                          >
                            a partir
                          </Typography>
                        </Typography>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation(); // ESSENCIAL
                          }}
                          sx={{
                            borderRadius: "14px",
                            fontWeight: 900,
                            px: 2,
                          }}
                        >
                          Ver ingressos
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Fade>
              </Slide>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}
